import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Navbar, Nav } from 'react-bootstrap';
import { NavLink, useLocation } from 'react-router-dom';
import { menuBarItems } from './constants';
import { UserProfileSettingsDropdown } from './UserProfileSettingsDropdown';
import { HelpMenu } from './HelpMenu';
import { mainMenuLinks } from './MainMenuDropdown/constants';
import { NotificationDropdown } from './NotificationDropdown';
import { MainMenuDropdown } from './MainMenuDropdown';
import { IconAbbLogo, IconPowertrainGuideCondition, IconPowertrainGuideMaintenance, IconPowertrainGuideDrive, IconPowertrainGuideRotating, IconPowertrainGuideHelp } from 'svgIcons/MotionPortalIcons';
import colors from 'theme/_colors.scss';
import useUserCapability from 'sharedHooks/useUserCapability';
import { SwitchOrganizationModal } from '../../SwitchOrganizationModal';
import { routes } from 'routes';
import { GeneralFilter } from '../../GeneralFilter';
import { PowertrainGuideAgent } from 'sharedComponents/PowertrainGuideAgent';
import CopilotKitApi from 'api/CopilotKitApi/copilotKitApi';
import appConfig from 'config/appConfig';
import { getEnvironmentStageSelector } from 'helpers/selectors';

const HeaderComponent = ({ selectedOrganization }) => {
    const { t: translate, i18n } = useTranslation();
    const userCapabilityHelper = useUserCapability();
    const [menuBarFits, setMenuBarFits] = useState(window.innerWidth > 1160);
    const location = useLocation();
    const environmentStage = useSelector(getEnvironmentStageSelector);
    const onlineHelpBaseUrl = mainMenuLinks.onlineHelpLinks[environmentStage];
    const onlineHelpUrl = onlineHelpBaseUrl ? `${onlineHelpBaseUrl}${i18n.language}` : '';

    const isREC = userCapabilityHelper.hasHomePageRecCapability(); /* FALSE FOR LSU AND END USER | TRUE FOR REC USER */
    const isLSU = userCapabilityHelper.hasFleetOverviewCapability() || userCapabilityHelper.hasHomePageCapability();
    const isNonPowertrainOrganization = selectedOrganization?.isPowertrainOrganization === false;

    const updateMenuVisibility = () => {
        setMenuBarFits(window.innerWidth > 1160);
    };

    useEffect(() => {
        window.addEventListener('resize', updateMenuVisibility, false);

        return () => {
            window.removeEventListener('resize', updateMenuVisibility, false);
        };
    }, []);

    const isActiveRoute = (route) => {
        if (route === routes.ConditionMonitoring) {
            return location.pathname.startsWith(routes.ConditionMonitoring) || location.pathname.startsWith(routes.PowertrainAndAssetDetails);
        }
        if (route === routes.Tools) {
            return location.pathname.startsWith(routes.Tools);
        }
        if (route === routes.FleetDashboard) {
            return location.pathname.startsWith(routes.FleetDashboard) || location.pathname.startsWith(routes.FleetOverview);
        }
        return location.pathname === route;
    };

    const renderMenuBarItems = () => {
        let menuItems = [...menuBarItems]; // Create a shallow copy to avoid mutating the original array
        if (!isREC && !isLSU) {
            menuItems = menuItems?.filter(item => item.route !== routes.FleetDashboard);
        }
        if (isNonPowertrainOrganization) {
            menuItems = menuItems?.filter(item => item.route === routes.Tools);
        }
        return menuBarFits && <Nav className='mx-auto'>
            {
                menuItems?.map((headerItem, key) =>
                    <Nav.Link
                        key={key}
                        as={NavLink}
                        to={headerItem.route}
                        isActive={() => isActiveRoute(headerItem.route)}
                        activeClassName='active'
                    >
                        {translate(headerItem.title)}
                    </Nav.Link>)
            }
        </Nav >;
    };

    return (
        <div className='header-component'>
            <Navbar className='pt-navbar-container' fluid='true'>
                <Navbar.Brand>
                    <span className='mo-portal-title'>
                        <IconAbbLogo height={20} viewBox='-1 -1 88.2 35' color={colors.red} width={52} />
                        <span className='brand-separator'></span>
                        <span className='brand-text'>
                            <span className='brand-text-bold'>ABB Ability&#8482;</span>
                            <span className='brand-text-light'>Digital Powertrain</span>
                        </span>
                    </span>
                </Navbar.Brand>
                <Navbar.Collapse>
                    {renderMenuBarItems()}
                    <Nav className='pt-navbar-right'>
                        <GeneralFilter isREC={isREC} />
                        {userCapabilityHelper.hasNotificationSettingsCapability() && !isNonPowertrainOrganization && <NotificationDropdown />}
                        <PowertrainGuideAgent
                            isStructuredChatEnabled={appConfig.enableA2UIChat}
                            streamChat={CopilotKitApi.streamDemoChat.bind(CopilotKitApi)}
                            labels={{
                                title: translate('ABB.Powertrain.Frontend.copilotAssistantTitle'),
                                trigger: translate('ABB.Powertrain.Frontend.copilotAssistantTriggerLabel'),
                                inputPlaceholder: translate('ABB.Powertrain.Frontend.copilotAssistantInputPlaceholder'),
                                welcomeTitle: translate('ABB.Powertrain.Frontend.copilotAssistantWelcomeTitle'),
                                welcomeSubtitle: translate('ABB.Powertrain.Frontend.copilotAssistantWelcomeSubtitle'),
                                thinking: translate('ABB.Powertrain.Frontend.copilotAssistantThinking'),
                                send: translate('ABB.Powertrain.Frontend.sendButton'),
                                sources: translate('ABB.Powertrain.Frontend.copilotAssistantSources'),
                                followups: translate('ABB.Powertrain.Frontend.copilotAssistantFollowups'),
                                minimize: translate('ABB.Powertrain.Frontend.copilotAssistantMinimize'),
                                restore: translate('ABB.Powertrain.Frontend.copilotAssistantRestore'),
                                expand: translate('ABB.Powertrain.Frontend.copilotAssistantExpand'),
                                close: translate('ABB.Powertrain.Frontend.copilotAssistantClose'),
                                refresh: translate('ABB.Powertrain.Frontend.copilotAssistantRefresh'),
                                error: translate('ABB.Powertrain.Frontend.somethingWentWrongTitle'),
                                selectAtLeast: translate('ABB.Powertrain.Frontend.copilotAssistantSelectAtLeast'),
                                options: translate('ABB.Powertrain.Frontend.copilotAssistantOptions'),
                                option: translate('ABB.Powertrain.Frontend.copilotAssistantOption'),
                                feedback: translate('ABB.Powertrain.Frontend.copilotAssistantFeedback'),
                                feedbackSubmitted: translate('ABB.Powertrain.Frontend.copilotAssistantFeedbackSubmitted'),
                                submit: translate('ABB.Powertrain.Frontend.submitButton'),
                                assistantImage: translate('ABB.Powertrain.Frontend.copilotAssistantImage'),
                                imageUnavailable: translate('ABB.Powertrain.Frontend.copilotAssistantImageUnavailable'),
                                pdfUnavailable: translate('ABB.Powertrain.Frontend.copilotAssistantPdfUnavailable'),
                                attachedDocument: translate('ABB.Powertrain.Frontend.copilotAssistantAttachedDocument'),
                                download: translate('ABB.Powertrain.Frontend.downloadButton'),
                                image: translate('ABB.Powertrain.Frontend.copilotAssistantImage'),
                                disclaimer: translate('ABB.Powertrain.Frontend.copilotAssistantDisclaimer'),
                                maxLengthError: translate('ABB.Powertrain.Frontend.copilotAssistantMaxLengthError')
                            }}
                            welcomeOptions={[
                                { id: 'condition', label: translate('ABB.Powertrain.Frontend.copilotAssistantConditionInsights'), prompt: translate('ABB.Powertrain.Frontend.copilotAssistantConditionPrompt'), icon: <IconPowertrainGuideCondition width={48} height={48} /> },
                                { id: 'maintenance', label: translate('ABB.Powertrain.Frontend.copilotAssistantMaintenanceInsights'), prompt: translate('ABB.Powertrain.Frontend.copilotAssistantMaintenancePrompt'), icon: <IconPowertrainGuideMaintenance width={48} height={48} /> },
                                { id: 'drives', label: translate('ABB.Powertrain.Frontend.copilotAssistantDrives'), prompt: translate('ABB.Powertrain.Frontend.copilotAssistantDrivesPrompt'), icon: <IconPowertrainGuideDrive width={48} height={48} /> },
                                { id: 'rotating', label: translate('ABB.Powertrain.Frontend.copilotAssistantRotatingEquipment'), prompt: translate('ABB.Powertrain.Frontend.copilotAssistantRotatingPrompt'), icon: <IconPowertrainGuideRotating width={48} height={48} /> },
                                { id: 'online-help', label: translate('ABB.Powertrain.Frontend.copilotAssistantOnlineHelp'), prompt: '', icon: <IconPowertrainGuideHelp width={48} height={48} />, href: onlineHelpUrl, showExternalLink: true }
                            ]}
                        />
                        <HelpMenu />
                        <UserProfileSettingsDropdown isNonPowertrainOrganization={isNonPowertrainOrganization} />
                        <MainMenuDropdown
                            menuBarFits={menuBarFits}
                            menuBarItems={menuBarItems}
                            isNonPowertrainOrganization={isNonPowertrainOrganization}
                        />
                    </Nav>
                </Navbar.Collapse>
            </Navbar>
            <SwitchOrganizationModal />
        </div >
    );
};

HeaderComponent.propTypes = {
    selectedOrganization: PropTypes.shape({
        organizationID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        isPowertrainOrganization: PropTypes.bool
    })
};

export default HeaderComponent;
